import TomSelect from 'tom-select';
import {defaultSwal, errorSwal} from "./sweetalert2.js";
import {get} from "../helper.js";

/**
 * Create Tom Select render settings.
 *
 * This is the single render contract for TomSelect option and item markup.
 * Keep spacing/thumbnail differences in SCSS, not in render templates.
 *
 * @param {Object} options
 * @param {string} options.labelField Field name TomSelect should read for visible option text. Defaults to `text`.
 * @param {string|null} options.imageField Field name for an optional thumbnail URL. Pass null to disable image rendering.
 * @param {string|null} options.iconField Field name for an optional Font Awesome class. Pass null to disable icon rendering.
 * @param {string|null} options.urlField Field name for an optional URL used by selected-item image buttons. Pass null to disable links.
 * @param {string|null} options.imageAlt Fixed alt text for rendered images. When null, the option label is used.
 * @param {string} options.imageAltFallback Alt text used when no fixed alt text or option label exists. Defaults to `Afbeelding`.
 * @returns {{item: function(*, *): string, option_create: function(*, *): string, no_results: function(*, *): string, option: function(*, *): string}}
 */
export const createTomSelectRender = ({
    labelField = 'text',
    imageField = 'image',
    iconField = 'icon',
    urlField = 'url',
    imageAlt = null,
    imageAltFallback = 'Afbeelding',
} = {}) => {
    // TomSelect data can use different field names per select, so every
    // renderable field is configurable through the factory options.
    const fieldValue = (data, field) => {
        return field ? data[field] : null;
    };

    // TomSelect passes its own escape function into render callbacks. Always use it
    // for user-visible data because option labels can come from server responses.
    const escapedLabel = (data, escape) => {
        return escape(fieldValue(data, labelField) ?? '');
    };

    const escapedImageAlt = (data, escape) => {
        const label = fieldValue(data, labelField);

        // Respect explicitly configured alt text first, including an empty alt.
        // Otherwise, use the label, because the thumbnail represents that option.
        return escape(imageAlt ?? (label || imageAltFallback));
    };

    const renderMedia = (data, escape, clickableImage = false) => {
        const icon = fieldValue(data, iconField);

        // Icons win over images to avoid rendering two leading visuals for one option.
        if (icon) {
            return `<i class="ts-icon fa ${escape(icon)}" aria-hidden="true"></i>`;
        }

        const image = fieldValue(data, imageField);

        if (!image) {
            return '';
        }

        const imageHtml = `<img src="${escape(image)}" class="ts-image" alt="${escapedImageAlt(data, escape)}">`;
        const url = fieldValue(data, urlField);

        // Only selected items use clickable image buttons; dropdown options should
        // select values, not navigate away from the current form.
        if (clickableImage && url) {
            return `<button type="button" data-url="${escape(url)}" class="ts-button">${imageHtml}</button>`;
        }

        return imageHtml;
    };

    const renderContent = (data, escape, clickableImage = false) => {
        // Keep the markup stable across every TomSelect caller. Visual differences
        // belong in SCSS, so render templates stay predictable.
        return `
            <div class="ts-content">
                ${renderMedia(data, escape, clickableImage)}
                <div class="ts-text">${escapedLabel(data, escape)}</div>
            </div>
        `;
    };

    return {
        option: (data, escape) => {
            return renderContent(data, escape);
        },
        item: (data, escape) => {
            return renderContent(data, escape, true);
        },
        option_create: (data, escape) => {
            return `<div class="create"><strong>${escape(data.input)}</strong> toevoegen &hellip;</div>`;
        },
        no_results: (data, escape) => {
            return `<div class="no-results">Geen resultaten gevonden voor '${escape(data.input)}'</div>`;
        }
    };
};

/**
 * Default Tom Select render settings.
 *
 * @type {{item: function(*, *): string, option_create: function(*, *): string, no_results: function(*, *): string, option: function(*, *): string}}
 */
export const defaultTomSelectRender = createTomSelectRender();

/**
 * @typedef {string|number|Array<string|number>|Object|null|undefined} TomSelectModelValue
 */

/**
 * @typedef {Object} LivewireSelectFields
 * @property {string} value Value field name.
 * @property {string} label Label field name.
 * @property {string|null} image Image field name.
 * @property {string|null} icon Icon field name.
 * @property {string|null} url URL field name.
 */

/**
 * @typedef {Object} LivewireSelectMethods
 * @property {string} search Livewire method used by lazy search.
 * @property {string|null} preload Optional Livewire method used to load selected option records.
 */

/**
 * @typedef {Object} LivewireSelectSettings
 * @property {boolean} create Allow newly typed options.
 * @property {boolean} disabled Start disabled.
 * @property {boolean} multiple Whether the select accepts multiple values.
 * @property {boolean} lazy Whether options are loaded through Livewire search.
 * @property {boolean} searchable Whether TomSelect should expose a text input.
 * @property {boolean} clearable Whether a clear button should be shown.
 * @property {boolean} loadOnFocus Whether lazy selects should load options for an empty query on focus.
 * @property {boolean} closeAfterSelect Whether the dropdown closes immediately after selecting.
 * @property {boolean} searchInDropdown Whether search input is rendered inside the dropdown.
 * @property {boolean} disableOnSelect Whether a meaningful value should disable the control.
 * @property {boolean} checkboxOptions Show checkboxes and keep selected options in the dropdown.
 * @property {boolean} selectionSummary Show a count instead of individual selected items.
 * @property {string} selectionSummarySingular Summary template for one selected item.
 * @property {string} selectionSummaryPlural Summary template for multiple selected items.
 * @property {string|null} dropdownParent TomSelect dropdown parent selector.
 * @property {string|null} dropdownSearchPlaceholder Placeholder for the dropdown search input.
 * @property {string|null} placeholder Placeholder for the closed control.
 */

/**
 * @typedef {Object} LivewireSelectDropdownHeader
 * @property {string|null} title Header title.
 * @property {string} headerClass Header wrapper class.
 * @property {string} titleRowClass Header title row class.
 * @property {string} labelClass Header label class.
 * @property {string} closeClass Header close button class.
 */

/**
 * @typedef {Object} LivewireSelectConfig
 * @property {Object} wire Livewire component proxy.
 * @property {*} value Entangled Livewire value.
 * @property {string} wireModel Wire model name used for events and element ids.
 * @property {LivewireSelectFields} fields TomSelect field names.
 * @property {Object[]} options Static TomSelect-ready option records.
 * @property {LivewireSelectMethods} methods Livewire method names for lazy loading.
 * @property {LivewireSelectSettings} settings Component behavior flags and TomSelect settings.
 * @property {LivewireSelectDropdownHeader} dropdownHeader Dropdown header plugin settings.
 */

/**
 * Create Alpine state for the Livewire select Blade component.
 *
 * @param {LivewireSelectConfig} config Component configuration generated by the Blade view.
 * @returns {Object}
 */
export const createLivewireSelect = (config) => {
    return {
        // Dependencies and entangled state passed in by the Blade component.
        wire: config.wire,
        value: config.value,
        wireModel: config.wireModel,

        // Field names and behavior flags stay configurable per select instance.
        fields: config.fields,
        methods: config.methods,
        settings: config.settings,
        dropdownHeader: config.dropdownHeader,

        // Blade already normalizes static options unless the select is lazy.
        optionsArray: config.options || [],

        // This Alpine object owns runtime state below after initialization.
        preloadedOptions: [],
        tomSelect: null,
        summaryElement: null,
        isRemoving: false,

        // Lazy searches can return out of order, so only the newest request may update options.
        latestLazyRequestId: 0,

        // Livewire may emit the selected item more than once during a render cycle.
        lastProcessedSelectedItemValue: null,

        /**
         * Initialize TomSelect after optional lazy preload data is available.
         *
         * Alpine calls this from x-init, so the Blade component stays a thin
         * configuration wrapper, and this helper owns the TomSelect lifecycle.
         *
         * @returns {Promise<void>}
         */
        async initTomSelect() {
            if (this.tomSelect) {
                // Alpine can re-run initialization after Livewire DOM updates; destroy
                // the previous instance before TomSelect wraps the same select again.
                this.tomSelect.destroy();
                this.summaryElement = null;
            }

            // Lazy selects need selected option records before TomSelect can show
            // readable labels for ids that were already present on page load.
            await this.preloadSelectedOptions();

            const tomSelectOptions = this.createTomSelectOptions();

            if (this.settings.lazy) {
                // Lazy selects receive their options from Livewire search calls.
                this.configureLazyLoading(tomSelectOptions);
            } else {
                // Static selects can be initialized with all options immediately.
                tomSelectOptions.options = this.optionsArray;
            }

            this.tomSelect = new TomSelect(this.$refs.select, tomSelectOptions);

            // TomSelect mutates its internal input elements during initialization,
            // so placeholder state is synced after the instance exists.
            this.syncPlaceholderInputs();

            this.tomSelect.on('dropdown_open', () => {
                // Dropdown-input is created/positioned by a plugin, so re-sync after
                // opening to keep custom dropdown placeholders visible.
                this.syncPlaceholderInputs();
            });

            // Apply one-time state after TomSelect has generated its control DOM.
            this.syncInitialDisabledState();
            this.updateTomSelectValue(this.value);

            // After initialization, keep both directions in sync:
            // TomSelect -> Livewire and Livewire -> TomSelect.
            this.watchLivewireValue();
            this.listenForLivewireEvents();
        },

        /**
         * Fetch display records for already-selected lazy values.
         *
         * Lazy selects do not receive the full option list on page load, so a
         * selected id needs its label/image/icon data before TomSelect renders.
         *
         * @returns {Promise<void>}
         */
        async preloadSelectedOptions() {
            this.preloadedOptions = [];

            if (!this.settings.lazy || !this.hasValue(this.value) || !this.methods.preload) {
                // Static selects already have their options, and empty lazy values
                // do not need display records.
                return;
            }

            if (typeof this.wire[this.methods.preload] !== 'function') {
                // The preload hook is optional; silently skip when the component
                // did not expose the configured method.
                return;
            }

            try {
                // The Livewire method may return one record or many records,
                // depending on whether this is a single or multi-select.
                this.preloadedOptions = await this.wire[this.methods.preload](this.value);
            }
            catch (error) {
                console.error('Error preloading option data:', error);
            }
        },

        /**
         * Build the base TomSelect settings shared by static and lazy selects.
         *
         * Lazy-specific load settings are added separately, so the empty-query
         * behavior stays isolated to callers that opt into it.
         *
         * @returns {Object} TomSelect configuration before static/lazy options are attached.
         */
        createTomSelectOptions() {
            const tomSelectOptions = {
                // The settings below map component props to TomSelect's API.
                create: this.settings.create,
                searchField: [this.fields.label],
                valueField: this.fields.value,
                labelField: this.fields.label,
                disabled: this.settings.disabled,
                maxOptions: 1000,
                plugins: this.createPlugins(),
                // Use the same renderer as the rest of the TomSelect helpers,
                // but with this component's configured field names.
                render: createTomSelectRender({
                    labelField: this.fields.label,
                    imageField: this.fields.image,
                    iconField: this.fields.icon,
                    urlField: this.fields.url,
                }),
                dropdownParent: this.settings.dropdownParent,
                placeholder: this.settings.selectionSummary ? '' : this.settings.placeholder,
                onItemRemove: (removedValue) => {
                    // Livewire components can listen for this when they need
                    // side effects beyond the raw model value update.
                    this.dispatchSelectEvent('select-value-removed', removedValue);
                },
                onItemAdd: (addedValue) => {
                    // Keep add/remove events symmetrical for component consumers.
                    this.dispatchSelectEvent('select-value-added', addedValue);
                },
                onChange: (selectedValue) => {
                    // Value sync is separated so the same conversion rules are
                    // reused for every TomSelect change.
                    this.handleTomSelectChange(selectedValue);
                },
            };

            if (this.settings.checkboxOptions) {
                tomSelectOptions.hideSelected = false;
            }

            if (this.settings.closeAfterSelect) {
                // Used by button-like pickers where the menu should close as soon
                // as a value is chosen.
                tomSelectOptions.closeAfterSelect = true;
            }

            if (!this.settings.searchable) {
                // `controlInput: null` tells TomSelect not to create a text input
                // inside the closed control. Without this, the user could still
                // type into the control even when our component says it is not searchable.
                tomSelectOptions.controlInput = null;

                // Empty `searchField` prevents TomSelect from filtering options
                // internally. Together with `controlInput: null`, this makes the
                // select behave like a plain picker instead of a search box.
                tomSelectOptions.searchField = [];
            }

            return tomSelectOptions;
        },

        /**
         * Build TomSelect plugin config from the component flags.
         *
         * TomSelect accepts configured plugins as an object, so this helper uses
         * object syntax for every plugin even when no custom options are needed.
         *
         * @returns {Object} TomSelect plugin configuration.
         */
        createPlugins() {
            const plugins = {};

            if (this.settings.multiple && !this.settings.selectionSummary) {
                // Multi-selects need per-item remove buttons because the selected
                // value is a list, not a single clearable value.
                plugins.remove_button = {};
            }

            if (this.settings.clearable) {
                // Single-selects use TomSelect's default clear button.
                plugins.clear_button = {};

                if (this.settings.multiple) {
                    // Multi-select clear-all needs custom text/title so the button
                    // clearly communicates that all selected items are removed.
                    plugins.clear_button = {
                        title: 'Alles verwijderen',
                        html: function (data) {
                            return `<div class="${data.className} btn" title="${data.title}">&times;</div>`;
                        }
                    };
                }
            }

            if (this.settings.checkboxOptions) {
                plugins.checkbox_options = {};
            }

            if (this.settings.searchInDropdown && this.settings.searchable) {
                // Moves search input into the dropdown while keeping the selected
                // value/placeholder visible in the control.
                plugins.dropdown_input = {};
            }

            if (this.dropdownHeader.title) {
                // The dropdown header plugin only exists when a title is passed.
                // Styling classes stay configurable for specific UI contexts.
                plugins.dropdown_header = {
                    title: this.dropdownHeader.title,
                    headerClass: this.dropdownHeader.headerClass,
                    titleRowClass: this.dropdownHeader.titleRowClass,
                    labelClass: this.dropdownHeader.labelClass,
                    closeClass: this.dropdownHeader.closeClass
                };
            }

            return plugins;
        },

        /**
         * Add lazy search behavior to a TomSelect config object.
         *
         * `loadOnFocus` intentionally allows an empty query because TomSelect
         * calls `load('')` when preloading on focus.
         *
         * @param {Object} tomSelectOptions TomSelect configuration object to mutate.
         * @returns {void}
         */
        configureLazyLoading(tomSelectOptions) {
            // Start with selected-value display records so the closed control can
            // render labels before the user searches.
            tomSelectOptions.options = this.preloadedOptions;
            tomSelectOptions.preload = this.settings.loadOnFocus ? 'focus' : false;
            tomSelectOptions.minLength = this.settings.loadOnFocus ? 0 : 1;

            if (this.settings.loadOnFocus) {
                // TomSelect blocks empty loads by default; this opt-in allows the
                // first focus/open action to fetch an initial option set.
                tomSelectOptions.shouldLoad = () => true;
            }

            tomSelectOptions.load = (query, callback) => {
                if (!query.length && !this.settings.loadOnFocus) {
                    // Without loadOnFocus, empty searches intentionally stay empty
                    // to avoid unnecessary Livewire requests.
                    callback();

                    return;
                }

                if (typeof this.wire[this.methods.search] !== 'function') {
                    // A configured lazy select cannot work without its search
                    // method, but failing gracefully keeps the form usable.
                    console.error(`Search method '${this.methods.search}' not found on Livewire component`);

                    callback();

                    return;
                }

                const requestId = ++this.latestLazyRequestId;

                // Capture a request id for this query. If a later query finishes
                // first, the older callback is ignored below.
                this.wire[this.methods.search](query)
                    .then(results => {
                        // A slower previous request should not replace the
                        // result set for a newer query the user already typed.
                        if (requestId !== this.latestLazyRequestId) {
                            callback();

                            return;
                        }

                        if (!query.length && this.settings.loadOnFocus && this.tomSelect) {
                            // Drop the selected-value-only preload records before
                            // adding the complete empty-query result set.
                            this.tomSelect.clearOptions(() => false);
                        }

                        callback(results);
                    })
                    .catch((error) => {
                        console.error(`Error calling ${this.methods.search}:`, error);

                        callback();
                    });
            };
        },

        /**
         * Dispatch a component-scoped Livewire event for add/remove hooks.
         *
         * @param {string} eventName Livewire event name.
         * @param {string|number} value Added or removed option value.
         * @returns {void}
         */
        dispatchSelectEvent(eventName, value) {
            this.wire.dispatchSelf(eventName, {
                // Include the model name so one Livewire component can distinguish
                // multiple select instances receiving the same event type.
                id: this.wireModel,
                value: value
            });
        },

        /**
         * Sync TomSelect changes back to the entangled Livewire property.
         *
         * @param {TomSelectModelValue} selectedValue Value emitted by TomSelect.
         * @returns {void}
         */
        handleTomSelectChange(selectedValue) {
            this.updateSelectionSummary();

            setTimeout(() => {
                if (this.settings.multiple) {
                    // Multi-select models must always receive an array. TomSelect
                    // normally emits an array here; if a scalar ever appears, keep
                    // it as a one-item selection instead of dropping it.
                    const valueToSync = this.normalizeMultiValue(selectedValue);

                    // Assign a new array reference so Alpine/Livewire detect the
                    // change even when values were mutated in place internally.
                    this.value = [...valueToSync];
                } else {
                    this.value = selectedValue;
                }

                // Some selects become disabled after receiving a meaningful value.
                this.syncDisabledStateFromValue(selectedValue);
            }, 0);
        },

        /**
         * Keep TomSelect in sync when Livewire changes the property externally.
         *
         * @returns {void}
         */
        watchLivewireValue() {
            this.$watch('value', (newValue) => {
                if (this.isRemoving) {
                    // File removal flows emit value changes while another component
                    // is cleaning up; skip TomSelect writes during that window.
                    return;
                }

                const currentTomValue = this.tomSelect.getValue();

                if (JSON.stringify(newValue) !== JSON.stringify(currentTomValue)) {
                    // Avoid resetting TomSelect when it already has the same value;
                    // unnecessary setValue calls can close dropdowns or move focus.
                    this.updateTomSelectValue(newValue);
                }

                // External Livewire updates may also need to toggle disabled state.
                this.syncDisabledStateFromValue(newValue);
            });
        },

        /**
         * Apply initially disabled behavior after TomSelect exists.
         *
         * @returns {void}
         */
        syncInitialDisabledState() {
            if (this.settings.disableOnSelect) {
                // Let the current value decide whether this starts enabled or disabled.
                this.syncDisabledStateFromValue(this.value);

                return;
            }

            if (this.settings.disabled) {
                // TomSelect mirrors the native disabled attribute only after init,
                // so explicitly apply it once the instance exists.
                this.updateDisabledState(true);
            }
        },

        /**
         * Listen for targeted Livewire events that update this select instance.
         *
         * @returns {void}
         */
        listenForLivewireEvents() {
            this.wire.on('options-updated', (data) => {
                if (data.target !== this.wireModel) {
                    // Events are component-scoped but may still target a different
                    // select on the same Livewire component.
                    return;
                }

                if (data.placeholder !== undefined) {
                    // Placeholder can change when the available option state changes.
                    this.updatePlaceholder(data.placeholder);
                }

                if (data.options) {
                    // Static option lists can be refreshed without a Livewire morph.
                    this.refreshOptions(data.options);
                }

                if (data.disabled !== undefined) {
                    // The server can enable/disable the select after option changes.
                    this.updateDisabledState(data.disabled);
                }
            });

            if (this.settings.lazy) {
                this.wire.on('selected-item-data', (data) => {
                    // Lazy selects may receive a single selected option record after
                    // another Livewire action changes the model.
                    this.handleSelectedItemData(data);
                });
            }
        },

        /**
         * Replace static options without letting Livewire morph the TomSelect DOM.
         *
         * @param {Object[]|Object} newOptions Runtime option list as records or value/label pairs.
         * @returns {void}
         */
        refreshOptions(newOptions) {
            if (!this.tomSelect || this.settings.lazy) {
                // Lazy selects are refreshed by search callbacks, not by replacing
                // their full option set here.
                return;
            }

            // Clear selected items before replacing options so stale selected
            // records are not kept alive by TomSelect's internal cache.
            this.tomSelect.clear(true);
            this.tomSelect.clearOptions();

            // Runtime updates accept the same two shapes as Blade props:
            // an array of records or an object keyed by value.
            const processedOptions = Array.isArray(newOptions)
                ? newOptions.map(option => this.normalizeOption(option))
                : Object.entries(newOptions).map(([value, label]) => this.normalizeOption(value, label));

            processedOptions.forEach(option => {
                this.tomSelect.addOption(option);
            });

            // Refresh the dropdown without opening it, then restore the model value
            // when it still exists in the new option list.
            this.tomSelect.refreshOptions(false);
            this.updateTomSelectValue(this.value);
        },

        /**
         * Convert runtime key/value options into the configured field shape.
         *
         * @param {Object|string|number} value Rich option record or option value.
         * @param {string|null} label Label used when `value` is a scalar.
         * @returns {Object} TomSelect-ready option record.
         */
        normalizeOption(value, label = null) {
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                // Rich option records already contain configured value/label fields
                // and may include image/icon/url metadata.
                return value;
            }

            // Key/value options are converted to the field names configured for
            // this select instance.
            return {
                [this.fields.value]: value,
                [this.fields.label]: label
            };
        },

        /**
         * Add one selected lazy option that arrives through Livewire events.
         *
         * The event may use the configured value/label fields or the common
         * `{ id, name }` shape used by simple option payloads.
         *
         * @param {Object|null} data Selected option payload.
         * @returns {void}
         */
        handleSelectedItemData(data) {
            if (!this.tomSelect || !data) {
                return;
            }

            // Prefer the configured value field, but accept `{ id }` payloads too.
            const optionValue = this.fieldValue(data, this.fields.value, 'id');

            if (!this.hasValue(optionValue)) {
                // Empty values are placeholders, not real options to add.
                return;
            }

            if (this.lastProcessedSelectedItemValue === optionValue) {
                // Ignore duplicate events for the same selected option.
                return;
            }

            this.lastProcessedSelectedItemValue = optionValue;

            // Prefer configured label, then common simple-option labels, then the
            // value itself so TomSelect always has visible selected text.
            const optionLabel = this.fieldValue(data, this.fields.label, 'name') ?? this.fieldValue(data, 'text', null) ?? optionValue;
            const option = {
                ...data,
                [this.fields.value]: optionValue,
                [this.fields.label]: optionLabel
            };

            // Add the missing option record before selecting it; otherwise TomSelect
            // can hold a raw value without knowing how to render the label.
            this.tomSelect.addOption(option);
            this.tomSelect.setValue(optionValue, true);
        },

        /**
         * Push the current Livewire value into TomSelect without firing change.
         *
         * @param {TomSelectModelValue} newValue Value from the entangled Livewire property.
         * @returns {void}
         */
        updateTomSelectValue(newValue) {
            if (!this.tomSelect) {
                return;
            }

            try {
                if (!this.settings.multiple && !this.hasValue(newValue)) {
                    // Empty single-select values should restore the placeholder
                    // instead of trying to select a blank option.
                    this.tomSelect.clear();

                    return;
                }

                let valueToSet = newValue;

                if (this.settings.multiple) {
                    // Livewire and TomSelect should both see multi-select values
                    // as arrays, even when hydration briefly produces another shape.
                    valueToSet = this.normalizeMultiValue(valueToSet);
                }

                // The second argument keeps this programmatic sync silent, avoiding
                // a feedback loop into the onChange handler.
                this.tomSelect.setValue(valueToSet, true);
                this.updateSelectionSummary();
            }
            catch (error) {
                console.error('Error updating TomSelect value:', error);
            }
        },

        /**
         * Show the selected count after user changes and silent Livewire updates.
         *
         * @returns {void}
         */
        updateSelectionSummary() {
            if (!this.tomSelect || !this.settings.selectionSummary) {
                return;
            }

            if (!this.summaryElement) {
                this.summaryElement = document.createElement('span');
                this.summaryElement.className = 'ts-selection-summary';
                this.tomSelect.control.prepend(this.summaryElement);
                this.tomSelect.wrapper.classList.add('has-selection-summary');
            }

            const count = this.tomSelect.items.length;
            const template = count === 1
                ? this.settings.selectionSummarySingular
                : this.settings.selectionSummaryPlural;

            this.summaryElement.textContent = count === 0
                ? (this.settings.placeholder || '')
                : template.replace(':count', count);
            this.summaryElement.classList.toggle('is-placeholder', count === 0);
            this.tomSelect.wrapper.classList.toggle('has-items', count > 0);
        },

        /**
         * Change the visible placeholder after initialization.
         *
         * @param {string|null} placeholder New control placeholder.
         * @returns {void}
         */
        updatePlaceholder(placeholder) {
            if (!this.tomSelect) {
                return;
            }

            // TomSelect stores placeholder in settings, then recalculates control
            // state through inputState().
            this.tomSelect.settings.placeholder = placeholder;
            this.tomSelect.inputState();
            this.syncPlaceholderInputs();
        },

        /**
         * Keep TomSelect's internal inputs aligned with the configured placeholder.
         *
         * @returns {void}
         */
        syncPlaceholderInputs() {
            if (!this.tomSelect) {
                return;
            }

            const placeholder = this.tomSelect.settings.placeholder || '';

            // TomSelect hides the original select and renders a generated control.
            // The original `placeholder` attribute is therefore no longer useful
            // for CSS or browser placeholder behavior after initialization.
            this.tomSelect.control.dataset.placeholder = placeholder;

            const controlPlaceholder = this.tomSelect.control.querySelector('.items-placeholder');

            if (controlPlaceholder) {
                // Some TomSelect states render a nested placeholder input. Keep it
                // aligned too, otherwise the visual placeholder and settings value
                // can drift after Livewire changes the placeholder.
                controlPlaceholder.placeholder = placeholder;
            }

            if (
                this.settings.searchInDropdown &&
                this.settings.dropdownSearchPlaceholder !== null &&
                this.tomSelect.control_input
            ) {
                // With dropdown_input, TomSelect reuses `control_input` as the
                // search field inside the dropdown. This placeholder is separate
                // from the closed-control placeholder, so it gets its own prop.
                //
                // `control_input` is absent when searchable=false/controlInput=null,
                // so the guard above prevents errors for non-searchable pickers.
                this.tomSelect.control_input.placeholder = this.settings.dropdownSearchPlaceholder;
            }
        },

        /**
         * Apply disable-on-select rules from a candidate value.
         *
         * @param {TomSelectModelValue} value Candidate value.
         * @returns {void}
         */
        syncDisabledStateFromValue(value) {
            if (!this.settings.disableOnSelect || !this.tomSelect) {
                return;
            }

            // `disableOnSelect` means any meaningful value locks the control.
            if (this.hasValue(value)) {
                this.updateDisabledState(true);
            } else {
                this.updateDisabledState(false);
            }
        },

        /**
         * Enable or disable the TomSelect instance.
         *
         * @param {boolean} disabled Whether the control should be disabled.
         * @returns {void}
         */
        updateDisabledState(disabled) {
            // Keep our settings object aligned so later checks use the latest state.
            this.settings.disabled = disabled;

            if (!this.tomSelect) {
                return;
            }

            if (this.settings.disabled) {
                this.tomSelect.disable();
            } else {
                this.tomSelect.enable();
            }
        },

        /**
         * Determine whether a single or multi-select value is meaningful.
         *
         * @param {TomSelectModelValue} value Value to inspect.
         * @returns {boolean}
         */
        hasValue(value) {
            if (Array.isArray(value)) {
                // Multi-select values are meaningful when at least one item exists.
                return value.length > 0;
            }

            // Single-select values treat null/undefined/empty string as empty.
            return value !== undefined && value !== null && value !== '';
        },

        /**
         * Normalize any incoming value into the array shape expected by multi-selects.
         *
         * @param {TomSelectModelValue} value Value emitted by TomSelect or hydrated by Livewire.
         * @returns {Array<string|number>} Multi-select value array.
         */
        normalizeMultiValue(value) {
            if (Array.isArray(value)) {
                return value;
            }

            if (!this.hasValue(value)) {
                return [];
            }

            if (typeof value === 'object') {
                // Livewire can hydrate array-like values as keyed objects.
                return Object.keys(value);
            }

            // Keep a scalar value if one appears; dropping it would silently lose
            // a valid selection.
            return [value];
        },

        /**
         * Read a configured data field with an optional fallback field.
         *
         * @param {Object} data Option data record.
         * @param {string|null} field Primary field name.
         * @param {string|null} fallbackField Fallback field name.
         * @returns {*|null}
         */
        fieldValue(data, field, fallbackField) {
            if (field && data[field] !== undefined) {
                // Configured fields take priority so rich option records work.
                return data[field];
            }

            if (fallbackField && data[fallbackField] !== undefined) {
                // Fallbacks keep simple option payloads readable.
                return data[fallbackField];
            }

            return null;
        }
    };
};

/**
 * Register the globally shared TomSelect helpers for Alpine and legacy callers.
 *
 * The shared Blade select component reads these helpers from `window`, so every
 * frontend entrypoint that renders the component should use this registration.
 */
export const registerGlobalTomSelectHelpers = () => {
    window.TomSelect = TomSelect;
    window.NLTTomSelect = {
        ...(window.NLTTomSelect || {}),
        createRender: createTomSelectRender,
        createLivewireSelect: createLivewireSelect,
    };
};

/**
 * Create a Tom Select instance with the default settings
 * @param {HTMLElement} element - The element to create the Tom Select instance for
 */
export const createDefaultTomSelect = (element) => {
    new TomSelect(element, {
        allowEmptyOption: true,
        maxOptions: null,
        plugins: [
            'dropdown_input',
        ],
        render: defaultTomSelectRender,
    });

    // TODO: Replace this functionality with a callback function instead
    document.querySelectorAll('.ts-button').forEach(button => {
        // Bind the click event listener to the Tom Select buttons
        button.addEventListener('click', function() {
            defaultSwal.fire({
                title               : 'Pagina verlaten',
                text                : 'Weet je zeker dat je de pagina wilt verlaten? Niet-opgeslagen wijzigingen gaan verloren.',
                icon                : 'info',
                confirmButtonText   : 'Doorgaan',
                cancelButtonText    : 'Annuleren'
            }).then((result) => {
                if (result.value) {
                    window.location.replace(button.dataset.url);
                }
            });
        });
    })
}

/**
 * Create a Tom Select instance with the multi-select settings
 * @param {HTMLElement} element - The element to create the Tom Select instance for
 */
export const createMultiSelectTomSelect = (element) => {
    new TomSelect(element, {
        closeAfterSelect: false,
        maxOptions: null,
        hideSelected: true,
        plugins: {
            'dropdown_input': {},
            'remove_button': {
                title: 'Verwijder',
            },
            'clear_button': {
                title: 'Alles verwijderen',
                html: function (data) {
                    return `<div class="${data.className} btn" title="${data.title}">&times;</div>`;
                }
            },
        },
        render: {
            option_create: defaultTomSelectRender.option_create,
            no_results: defaultTomSelectRender.no_results,
        }
    });
}

/**
 * Create a Tom Select instance with the delimiter settings
 * @param {HTMLElement} element - The element to create the Tom Select instance for
 */
export const createDelimiterTomSelect = (element) => {
    new TomSelect(element, {
        create: true,
        maxOptions: null,
        delimiter: ',',
        plugins: [
            'dropdown_input',
            'restore_on_backspace',
            'caret_position',
            'remove_button',
            'clear_button',
            'input_autogrow'
        ],
        render: {
            option_create: defaultTomSelectRender.option_create,
            no_results: defaultTomSelectRender.no_results,
        }
    });
}

/**
 * Create a Tom Select instance with the async settings
 * @param {HTMLElement} element - The element to create the Tom Select instance for
 * @param {string|null} url - The URL to fetch the data from
 *
 * @param {Object} options - The options to set for the Tom Select
 * @param {boolean} options.preload - Preload the data
 * @param {number|null} options.maxOptions - The maximum amount of options to show
 * @param {string} options.valueField - The value field to use
 * @param {string} options.labelField - The label field to use
 * @param {Array} options.searchField - The search field to use
 * @param {Object} options.render - The render template object to use
 * @param {string|null} options.dropdownName - The name of the dropdown (used in messages so the user knows which dropdown is meant)
 * @param {boolean} options.disableFetchingDataAfterInitialLoad - Disable fetching data after the initial load
 */
export const createAsyncTomSelect = (element, url = null, {
    preload = true,
    maxOptions = null,
    valueField = 'id',
    labelField = 'text',
    searchField = [{field:'id', weight:2},{field:'text',weight:0.5}],
    render = defaultTomSelectRender,
    dropdownName = null,
    disableFetchingDataAfterInitialLoad = true,
} = {}) => {
    // Prepare the dropdown name for the error message
    const errorMessageDropdownName = dropdownName ? ` (${dropdownName})` : '';

    const tomSelect = new TomSelect(element, {
        allowEmptyOption: true,
        create: false,
        // TODO: Add placeholder support
        // placeholder: 'Deelnemer',
        valueField,
        labelField,
        searchField,
        preload: preload,
        maxOptions: maxOptions,
        plugins: {
            'dropdown_input': {},
            'clear_button': {
                title: 'Alles verwijderen',
                html: function (data) {
                    return `<div class="${data.className} btn" title="${data.title}">&times;</div>`;
                }
            },
        },
        load: function(query, callback) {
            // Prevent multiple requests
            const self = this;
            if (self.loading > 1) {
                callback();
                return;
            }


            // Check if the URL exists, if not show an error message
            if(!url) {
                console.error('[DVS][TomSelect][createAsyncTomSelect] No URL provided to fetch the tom-select data from', {
                    element,
                });

                callback();
            } else {
                get(url)
                    .then(data => {
                        // Sort the items based on the provided algorithm
                        const sortedItems = Object.entries([
                            { id: '', text: '-' },
                            ...data.items,
                        ])
                            .sort(([aKey , aValue], [bKey , bValue]) => {
                                const aText = aValue.text;
                                const bText = bValue.text;
                                if (aText === '-') return -1;
                                if (bText === '-') return 1;
                                return aText.localeCompare(bText);
                            });

                        // Pass the sorted items to the callback
                        callback(sortedItems);

                        if(disableFetchingDataAfterInitialLoad) {
                            // Disable the load function to prevent further requests after the initial data load
                            self.settings.load = null;
                        }
                    }).catch(error => {
                        console.error('[DVS][TomSelect][createAsyncTomSelect] Error fetching the data from the URL', {
                            element,
                            error,
                        });

                        errorSwal.fire({
                            title: 'Kan gegevens niet ophalen',
                            text: `Door een fout is het niet mogelijk om de gegevens op te halen om de dropdown${errorMessageDropdownName} te vullen. Probeer het later opnieuw.`,
                        });

                        callback();
                    });
            }
        },
        render,
    });
}
